/**
 * Cross-table Single-Row View shell (Concern 1).
 *
 * Wraps `<SingleRowView />` for the case where the active row-node in the
 * breadcrumb tree belongs to a DIFFERENT table than the underlying TE grid.
 * The row is fetched by `useSingleRowData` in the parent and passed in;
 * this shell only handles the loading / error / ready render branches.
 *
 * Kept small + dependency-light so `TableExplorerTab.tsx` can stay focused
 * on its existing wiring and doesn't have to inline another render branch.
 */

import { Button } from '../../../../components/ui/button';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import { SingleRowView } from '../../single-row';
import type { SingleRowNodeRef } from '../../single-row';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { DatabaseEngineId } from '../../../../core/engines';

export type CrossTableSingleRowViewProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly engine: DatabaseEngineId;
  readonly nodeRef: SingleRowNodeRef;
  readonly row: Record<string, unknown> | null;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  readonly inboundForeignKeys: ReadonlyArray<TableExplorerInboundForeignKey>;
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly onClose: () => void;
  readonly onFkClick?: (args: {
    column: string;
    value: unknown;
    modifierKey: boolean;
  }) => void;
  readonly onRelatedSectionOpen?: (args: {
    relation: import('../../single-row/types/singleRowTypes').InboundRelation;
  }) => void;
  readonly onRelatedRowOpen?: (args: {
    relation: import('../../single-row/types/singleRowTypes').InboundRelation;
    row: Record<string, unknown>;
    via: 'icon' | 'context-menu';
    modifierKey: boolean;
  }) => void;
};

export function CrossTableSingleRowView(props: CrossTableSingleRowViewProps) {
  const {
    row,
    columns,
    isLoading,
    error,
    onRetry,
    onClose,
    inboundForeignKeys,
    ...rest
  } = props;

  if (isLoading && !row) {
    return (
      <div
        className="flex-1 min-h-0 overflow-hidden flex items-center justify-center text-xs text-muted-foreground"
        data-testid="cross-table-single-row-loading"
      >
        <RandomDotmSpinner size={20} />
        <span className="ml-2">Loading row…</span>
      </div>
    );
  }

  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div
        className="flex-1 min-h-0 overflow-hidden flex flex-col items-center justify-center gap-2 px-4 text-xs"
        data-testid="cross-table-single-row-error"
      >
        <div className="text-destructive">Failed to load row: {message}</div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRetry}
            data-testid="cross-table-single-row-retry"
          >
            Retry
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Back to grid
          </Button>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div
        className="flex-1 min-h-0 overflow-hidden flex flex-col items-center justify-center gap-2 px-4 text-xs text-muted-foreground"
        data-testid="cross-table-single-row-empty"
      >
        <div>No row found matching the active primary key.</div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Back to grid
        </Button>
      </div>
    );
  }

  return (
    <SingleRowView
      {...rest}
      row={row}
      columns={columns}
      inboundForeignKeys={inboundForeignKeys}
      onClose={onClose}
    />
  );
}
